<template>
	<q-dialog
		ref="dialogRef"
		persistent
		@hide="onDialogHide"
	>
		<q-card class="q-dialog-plugin bulk-import-dialog">
			<div class="flex justify-end bg-grey-4">
				<q-btn
					dense
					rounded
					unelevated
					@click="onDialogHide"
				>
					<q-icon
						name="close"
						size="sm"
					/>
				</q-btn>
			</div>

			<div class="q-px-lg q-pb-md q-pt-md">
				<div class="text-h6 q-mb-sm">Bulk import</div>
				<div class="text-caption text-grey-7 q-mb-md">
					Paste JSON with word, translate and optional example fields.
				</div>

				<q-input
					v-model="jsonInput"
					type="textarea"
					filled
					autogrow
					label="JSON"
					:rows="6"
					class="q-mb-sm"
					@update:model-value="parseJson"
				/>

				<div class="row items-center q-mb-md">
					<q-btn
						flat
						dense
						color="primary"
						icon="content_copy"
						label="Copy example"
						@click="copyExample"
					/>
				</div>

				<q-banner
					v-if="parseError"
					class="bg-red-1 text-red-9 q-mb-md"
					rounded
					dense
				>
					{{ parseError }}
				</q-banner>

				<template v-if="items.length">
					<div class="row items-center justify-between q-mb-sm">
						<div class="text-subtitle2">Preview ({{ items.length }})</div>
						<q-btn
							flat
							dense
							color="primary"
							label="Select all new"
							:disable="!hasUnselectedNewItems"
							@click="selectAllNew"
						/>
					</div>

					<q-list
						bordered
						class="rounded-borders preview-list q-mb-md"
					>
						<q-item
							:key="index"
							v-for="(item, index) in items"
							:class="{ 'invalid-item': !item.isValid }"
						>
							<q-item-section side>
								<q-checkbox
									v-model="item.selected"
									:disable="!item.isValid"
									color="primary"
								/>
							</q-item-section>

							<q-item-section>
								<q-item-label :class="{ 'text-negative': !item.isValid }">
									{{ item.word || 'Missing word' }}
								</q-item-label>
								<q-item-label caption>
									{{ item.translate || 'Missing translate' }}
								</q-item-label>
								<q-item-label
									v-if="item.example"
									caption
									class="text-grey-7"
								>
									{{ item.example }}
								</q-item-label>
							</q-item-section>

							<q-item-section
								v-if="item.isDuplicate"
								side
							>
								<q-badge color="orange">Already exists</q-badge>
							</q-item-section>
						</q-item>
					</q-list>
				</template>

				<div class="row justify-end q-gutter-sm">
					<q-btn
						flat
						label="Cancel"
						color="primary"
						@click="onDialogHide"
					/>
					<q-btn
						color="primary"
						icon="upload"
						:label="uploadLabel"
						:loading="isUploading"
						:disable="selectedCount === 0 || !!parseError"
						@click="onUpload"
					/>
				</div>
			</div>
		</q-card>
	</q-dialog>
</template>

<script setup>
import { useDialogPluginComponent } from 'quasar'
import { computed, ref } from 'vue'
import { useClipboard } from '@vueuse/core'
import { useCardsStore } from '@/stores/cards'
import { BULK_IMPORT_JSON_EXAMPLE, parseBulkWordsJson } from '@/helpers/bulkImport'
import $snackBar from '@/services/snackBar'

defineEmits([...useDialogPluginComponent.emits])

const cardsStore = useCardsStore()
const { dialogRef, onDialogHide } = useDialogPluginComponent()
const { copy } = useClipboard()

const jsonInput = ref('')
const items = ref([])
const parseError = ref(null)
const isUploading = ref(false)

const selectedCount = computed(() => items.value.filter((item) => item.selected && item.isValid).length)
const uploadLabel = computed(() => `Upload (${selectedCount.value})`)
const hasUnselectedNewItems = computed(() =>
	items.value.some((item) => item.isValid && !item.isDuplicate && !item.selected)
)

const parseJson = () => {
	const { items: parsedItems, error } = parseBulkWordsJson(jsonInput.value, cardsStore.cards)
	items.value = parsedItems
	parseError.value = error
}

const selectAllNew = () => {
	items.value.forEach((item) => {
		if (item.isValid && !item.isDuplicate) item.selected = true
	})
}

const copyExample = () => {
	copy(BULK_IMPORT_JSON_EXAMPLE)
	$snackBar.success('Copied!')
}

const onUpload = async () => {
	const wordsToUpload = items.value
		.filter((item) => item.selected && item.isValid)
		.map(({ word, translate, example }) => ({ word, translate, example }))

	if (!wordsToUpload.length) return

	isUploading.value = true
	try {
		await cardsStore.bulkAddWords(wordsToUpload)
		onDialogHide()
	} finally {
		isUploading.value = false
	}
}
</script>

<style lang="scss" scoped>
.bulk-import-dialog {
	width: min(560px, 95vw);
	max-width: 95vw;
}

.preview-list {
	max-height: 280px;
	overflow: auto;
}

.invalid-item {
	background-color: #ffebee;
}
</style>
